/**
 * NCR Detail Tab (Step 1) for NCR Edit.
 *
 * Layout: stacked bordered cards grouping fields by concept
 * (Customer & Supplier · Order Information · Reject Details & Action).
 * Customer-requested redesign — replaces the prior 2-column "Main Detail
 * + Non-Conformance Detail" split. All field bindings, validation,
 * permissions and workflow tabs below are unchanged.
 */

import type { ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Save, Send } from 'lucide-react';
import { NonConformanceReport, UserProfile, SourceOfRejection, FaultCodeGroup, FaultCodeCategory } from '../../lib/api';
import { NCREditFormData } from './types';

interface Step1TabProps {
  ncr: NonConformanceReport;
  formData: NCREditFormData;
  canEdit: boolean;
  saving: boolean;
  customerUsers: UserProfile[];
  sourcesOfRejection: SourceOfRejection[];
  faultGroups: FaultCodeGroup[];
  faultCategories: FaultCodeCategory[];
  onUpdateFormData: (field: string, value: unknown) => void;
  onSave: () => void;
  onSubmitDetail: () => void;
  onCloseNo8D: () => void;
}

/** Bordered grouped section. Plain title bar, no chevron, no numbering. */
function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card>
      <CardHeader className="pb-3 border-b bg-muted/30">
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 pt-4">{children}</CardContent>
    </Card>
  );
}

export function NCRDetailTab({
  ncr,
  formData,
  canEdit,
  saving,
  customerUsers,
  sourcesOfRejection,
  faultGroups,
  faultCategories,
  onUpdateFormData,
  onSave,
  onSubmitDetail,
  onCloseNo8D,
}: Step1TabProps) {
  // Filter categories by selected fault group
  const selectedFaultGroup = (formData.fault_group as number | undefined) ?? ncr.fault_group ?? null;
  const filteredCategories = selectedFaultGroup
    ? faultCategories.filter((c) => c.fault_group === selectedFaultGroup)
    : [];
  const liabilityType = (formData.liability_type ?? ncr.liability_type ?? 0) as number;
  const collectionRequired = formData.is_collection_required ?? ncr.is_collection_required;
  const creditRequired = formData.is_credit_required ?? ncr.is_credit_required;

  return (
    <>
      <div className="space-y-6">
        {/* ===== Section 1 — Customer & Supplier ===== */}
        <Section title="Customer & Supplier">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Customer</Label>
              <Input value={ncr.company_name} disabled />
            </div>
            <div>
              <Label>Supplier</Label>
              <Input value={ncr.supplier_name} disabled={!canEdit} />
            </div>
            <div>
              <Label>Raised By</Label>
              <Input value={ncr.raised_by_name} disabled />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Company NCR/SRN No.</Label>
              <Input value={ncr.ncr_number || '—'} disabled />
            </div>
            <div>
              <Label>NCR Date</Label>
              <Input value={ncr.ncr_date ? new Date(ncr.ncr_date).toLocaleDateString() : '—'} disabled />
            </div>
            <div>
              <Label>Severity</Label>
              <select
                value={formData.severity || 'minor'}
                onChange={(e) => onUpdateFormData('severity', e.target.value)}
                disabled={!canEdit}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
              >
                <option value="minor">Minor</option>
                <option value="major">Major</option>
                <option value="critical">Critical</option>
              </select>
            </div>
          </div>
        </Section>

        {/* ===== Section 2 — Order Information ===== */}
        <Section title="Order Information">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>GRN No.</Label>
              <Input
                value={formData.grn_number || ''}
                onChange={(e) => onUpdateFormData('grn_number', e.target.value)}
                disabled={!canEdit}
              />
            </div>
            <div>
              <Label>PO No.</Label>
              <Input
                value={formData.po_number || ''}
                onChange={(e) => onUpdateFormData('po_number', e.target.value)}
                disabled={!canEdit}
              />
            </div>
            <div>
              <Label>PO Line No.</Label>
              {/* CR-5537: was rendering formData.po_line (the FK id, e.g.
                  '123') instead of the actual PO line number. Use the new
                  po_line_number field surfaced by NCRListSerializer (CR-5535)
                  which sources from po_line.line_number. */}
              <Input value={formData.po_line_number != null ? String(formData.po_line_number) : ''} disabled />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Part No.</Label>
              <Input
                value={formData.part_number || ''}
                onChange={(e) => onUpdateFormData('part_number', e.target.value)}
                disabled={!canEdit}
              />
            </div>
            <div className="md:col-span-2">
              <Label>Part Description</Label>
              <Input
                value={formData.description || ''}
                onChange={(e) => onUpdateFormData('description', e.target.value)}
                disabled={!canEdit}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Batch No.</Label>
              <Input
                value={formData.batch_number || ''}
                onChange={(e) => onUpdateFormData('batch_number', e.target.value)}
                disabled={!canEdit}
              />
            </div>
            <div>
              <Label>Works Order No.</Label>
              <Input
                value={(formData['works_order'] as string) || ''}
                onChange={(e) => onUpdateFormData('works_order', e.target.value)}
                disabled={!canEdit}
              />
            </div>
            <div>
              <Label>Buyer</Label>
              <select
                value={formData.buyer_name || ''}
                onChange={(e) => onUpdateFormData('buyer_name', e.target.value)}
                disabled={!canEdit}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
              >
                <option value="">Select Buyer...</option>
                {customerUsers.map((u) => (
                  <option key={u.id} value={`${u.user.first_name} ${u.user.last_name}`.trim()}>
                    {`${u.user.first_name} ${u.user.last_name}`.trim() || u.user.email}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Batch Qty</Label>
              <Input
                type="number"
                value={formData.batch_qty || 0}
                onChange={(e) => onUpdateFormData('batch_qty', parseInt(e.target.value))}
                disabled={!canEdit}
              />
            </div>
            <div>
              <Label>Unit Price</Label>
              <Input
                value={formData.unit_price || ''}
                onChange={(e) => onUpdateFormData('unit_price', e.target.value)}
                disabled={!canEdit}
              />
            </div>
          </div>
        </Section>

        {/* ===== Section 3 — Reject Details & Action ===== */}
        <Section title="Reject Details & Action">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Reject Qty</Label>
              <Input
                type="number"
                value={formData.reject_qty || 0}
                onChange={(e) => onUpdateFormData('reject_qty', parseInt(e.target.value))}
                disabled={!canEdit}
              />
            </div>
            <div>
              <Label>Shortage Qty</Label>
              <Input
                type="number"
                value={formData.shortage_qty || 0}
                onChange={(e) => onUpdateFormData('shortage_qty', parseInt(e.target.value))}
                disabled={!canEdit}
              />
            </div>
            <div>
              <Label>Source of Rejection</Label>
              {canEdit ? (
                <select
                  value={(formData.source_of_rejection_option as number) ?? ncr.source_of_rejection_option ?? ''}
                  onChange={(e) => onUpdateFormData('source_of_rejection_option', e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                >
                  <option value="">Select...</option>
                  {sourcesOfRejection.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              ) : (
                <Input value={ncr.source_of_rejection_option_name || '—'} disabled />
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Fault Group</Label>
              {canEdit ? (
                <select
                  value={selectedFaultGroup ?? ''}
                  onChange={(e) => {
                    const val = e.target.value ? parseInt(e.target.value) : null;
                    onUpdateFormData('fault_group', val);
                    onUpdateFormData('fault_category', null);
                  }}
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                >
                  <option value="">Select...</option>
                  {faultGroups.map((g) => (
                    <option key={g.id} value={g.id}>{g.name}</option>
                  ))}
                </select>
              ) : (
                <Input value={ncr.fault_group_name || '—'} disabled />
              )}
            </div>
            <div>
              <Label>Fault Category</Label>
              {canEdit ? (
                <select
                  value={(formData.fault_category as number) ?? ncr.fault_category ?? ''}
                  onChange={(e) => onUpdateFormData('fault_category', e.target.value ? parseInt(e.target.value) : null)}
                  disabled={!selectedFaultGroup}
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                >
                  <option value="">{selectedFaultGroup ? 'Select...' : 'Select fault group first'}</option>
                  {filteredCategories.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              ) : (
                <Input value={ncr.fault_category_name || '—'} disabled />
              )}
            </div>
          </div>

          <div>
            <Label>Description of Non-Conformity</Label>
            <Textarea
              value={formData.defect_description || ''}
              onChange={(e) => onUpdateFormData('defect_description', e.target.value)}
              disabled={!canEdit}
              rows={4}
              maxLength={1000}
            />
            <p className="text-xs text-muted-foreground mt-1">
              {(formData.defect_description || '').length}/1000 characters
            </p>
          </div>

          {/* Liability — kept inline because it gates the action sub-block */}
          <div className="border-t pt-4 mt-2">
            <h4 className="text-sm font-semibold mb-1">Liability <span className="text-red-500">*</span></h4>
            {canEdit && <p className="text-xs text-muted-foreground mb-3">Please Select</p>}
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => canEdit && onUpdateFormData('liability_type', 2)}
                disabled={!canEdit}
                className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                  liabilityType === 2
                    ? 'border-emerald-500 text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300 dark:border-gray-700 dark:text-gray-400'
                } ${!canEdit ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                Customer Liability
              </button>
              <button
                type="button"
                onClick={() => canEdit && onUpdateFormData('liability_type', 1)}
                disabled={!canEdit}
                className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                  liabilityType === 1
                    ? 'border-emerald-500 text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300 dark:border-gray-700 dark:text-gray-400'
                } ${!canEdit ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                Supplier Liability
              </button>
            </div>

            {liabilityType > 0 && (
              <div className="mt-4">
                <p className="text-xs font-semibold text-muted-foreground mb-3">
                  Required actions for the {liabilityType === 1 ? 'supplier' : 'customer'}:
                </p>
                <div className={`grid ${liabilityType === 1 ? 'grid-cols-3' : 'grid-cols-2'} gap-3`}>
                  <div>
                    <Label className="text-xs">8D Required <span className="text-red-500">*</span></Label>
                    <select
                      value={(formData.is_8d_required ?? ncr.is_8d_required) ? 'yes' : ((formData.is_8d_required ?? ncr.is_8d_required) === false ? 'no' : '')}
                      onChange={(e) => onUpdateFormData('is_8d_required', e.target.value === 'yes')}
                      disabled={!canEdit}
                      className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                    >
                      <option value="">Please Select</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs">Collection Required <span className="text-red-500">*</span></Label>
                    <select
                      value={collectionRequired ? 'yes' : (collectionRequired === false ? 'no' : '')}
                      onChange={(e) => onUpdateFormData('is_collection_required', e.target.value === 'yes')}
                      disabled={!canEdit}
                      className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                    >
                      <option value="">Please Select</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  </div>
                  {liabilityType === 1 && (
                    <div>
                      <Label className="text-xs">Credit Required <span className="text-red-500">*</span></Label>
                      <select
                        value={creditRequired ? 'yes' : (creditRequired === false ? 'no' : '')}
                        onChange={(e) => onUpdateFormData('is_credit_required', e.target.value === 'yes')}
                        disabled={!canEdit}
                        className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                      >
                        <option value="">Please Select</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                    </div>
                  )}
                </div>

                {(collectionRequired || creditRequired) && (
                  <div className="mt-4 pt-3 border-t border-dashed space-y-3">
                    <p className="text-xs font-semibold text-muted-foreground">
                      Disposition &amp; Collection Details:
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Proposed Disposition <span className="text-red-500">*</span></Label>
                        <select
                          value={formData.proposed_rejection_type ?? ncr.proposed_rejection_type ?? 0}
                          onChange={(e) => onUpdateFormData('proposed_rejection_type', parseInt(e.target.value))}
                          disabled={!canEdit}
                          className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                        >
                          <option value={0}>Please Select</option>
                          <option value={1}>Return to Supplier</option>
                          <option value={2}>Scrap</option>
                          <option value={3}>Rework</option>
                          <option value={4}>Use As Is</option>
                          <option value={5}>Sort</option>
                        </select>
                      </div>
                      <div>
                        <Label className="text-xs">Disposition Qty</Label>
                        <Input
                          type="number"
                          value={formData.disposition_quantity ?? ncr.disposition_quantity ?? ''}
                          onChange={(e) => onUpdateFormData('disposition_quantity', e.target.value ? parseInt(e.target.value) : null)}
                          disabled={!canEdit}
                          className="h-9"
                          placeholder="Qty"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Action Required <span className="text-red-500">*</span></Label>
                      <select
                        value={formData.action_required ?? ncr.action_required ?? 0}
                        onChange={(e) => onUpdateFormData('action_required', parseInt(e.target.value))}
                        disabled={!canEdit}
                        className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                      >
                        <option value={0}>None</option>
                        <option value={1}>Containment Only</option>
                        <option value={2}>Corrective Action</option>
                        <option value={3}>Full 8D</option>
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs">Liability Classification</Label>
                      <Input
                        value={formData.liability_classification ?? ncr.liability_classification ?? ''}
                        onChange={(e) => onUpdateFormData('liability_classification', e.target.value)}
                        disabled={!canEdit}
                        className="h-9"
                        placeholder="e.g. Supplier manufacturing defect"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {(ncr.closed_date || ncr.raised_date) && (
            <div className="border-t pt-4 mt-2">
              <h4 className="text-sm font-semibold mb-3">Key Dates</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                {ncr.raised_date && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Raised Date</Label>
                    <p className="font-medium">{new Date(ncr.raised_date).toLocaleDateString()}</p>
                  </div>
                )}
                {ncr.closed_date && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Closed Date</Label>
                    <p className="font-medium">{new Date(ncr.closed_date).toLocaleDateString()}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </Section>
      </div>

      {/* Submit buttons below the grouped sections */}
      {canEdit && (
        <div className="flex gap-2 mt-4">
          <Button onClick={onSave} disabled={saving} variant="outline">
            <Save className="h-4 w-4 mr-2" />
            Save
          </Button>
          <Button onClick={onSubmitDetail} disabled={saving}>
            <Send className="h-4 w-4 mr-2" />
            Submit
          </Button>
          {formData.is_8d_required === false && (
            <Button onClick={onCloseNo8D} disabled={saving} variant="destructive">
              Close - No 8D Required
            </Button>
          )}
        </div>
      )}
    </>
  );
}
